import type { ScheduleEvent, BoxScore, ScheduleEventGameData } from '~/types'
import { Status, StatusLabel } from '~/enums/schedule-event'

export const useBoxScore = (srIdRef: MaybeRefOrGetter<Maybe<string | undefined>>) => {
  const srId = ref<Maybe<string>>()

  const { getStreamById } = useLiveGamesStore()

  const {
    clock: liveClock,
    currentPeriodText: liveCurrentPeriodText,
    status: liveStatus,
    homePoints: liveHomePoints,
    awayPoints: liveAwayPoints,
  } = useGameData(srId)

  const stream = computed<Maybe<ScheduleEventGameData.Model>>(() => getStreamById(srId.value))

  const hasBoxScoreData = computed<boolean>(() => null !== stream.value && null !== stream.value.boxScore)
  const boxScoreData = computed<Maybe<BoxScore.Model>>(() => hasBoxScoreData.value ? stream.value!.boxScore! : null)

  const periodAmounts = computed<number>(() => hasBoxScoreData.value ? boxScoreData.value!.scoring.length : 0)
  const scoringData = computed<BoxScore.Scoring[]>(() => hasBoxScoreData.value ? boxScoreData.value!.scoring : [])
  const summaryData = computed<Maybe<BoxScore.Summary>>(() => hasBoxScoreData.value ? boxScoreData.value!.summary : null)
  const homeTeam = computed<Maybe<BoxScore.SummaryTeam>>(() => hasBoxScoreData.value ? boxScoreData.value!.summary.home : null)
  const homeTeamRecords = computed<Maybe<string>>(() => homeTeam.value && homeTeam.value?.record ? `${(homeTeam.value.record?.wins || 0)} - ${(homeTeam.value.record?.losses || 0)}` : null)
  const homePoints = computed<Maybe<number>>(() => liveHomePoints.value ?? boxScoreData.value?.summary?.home?.points ?? null)
  const awayTeam = computed<Maybe<BoxScore.SummaryTeam>>(() => hasBoxScoreData.value ? boxScoreData.value!.summary.away : null)
  const awayTeamRecords = computed<Maybe<string>>(() => awayTeam.value && awayTeam.value?.record ? `${(awayTeam.value.record?.wins || 0)} - ${(awayTeam.value.record?.losses || 0)}` : null)
  const awayPoints = computed<Maybe<number>>(() => liveAwayPoints.value ?? boxScoreData.value?.summary?.away?.points ?? null)
  const clock = computed<Maybe<string>>(() => liveClock.value ?? boxScoreData.value?.clock ?? null)
  const status = computed<Maybe<ScheduleEvent.Status>>(() => liveStatus.value ?? boxScoreData.value?.status ?? null)
  const clockString = computed<Maybe<string>>(() => {
    if (status.value !== Status.InProgress) {
      return null
    }

    return clock.value
  })
  const currentPeriodText = computed<Maybe<string>>(() => {
    if (status.value && [Status.InProgress, Status.Halftime].includes(status.value)) {
      return liveCurrentPeriodText.value
    }

    switch (status.value) {
      case Status.Closed:
      case Status.Complete:
        return StatusLabel.Complete
      case Status.Halftime:
        return StatusLabel.Halftime
      case Status.Postponed:
        return StatusLabel.Postponed
      case Status.Suspended:
        return StatusLabel.Suspended
      case Status.Delayed:
        return StatusLabel.Delayed
      case Status.Cancelled:
        return StatusLabel.Cancelled
      case Status.TimeTbd:
        return StatusLabel.TimeTbd
      default:
        return null
    }
  })
  const scoringPlays = computed<BoxScore.ScoringPlay[]>(() => hasBoxScoreData.value ? boxScoreData.value!.scoringPlays : [])
  const scoringDrives = computed<BoxScore.ScoringDrive[]>(() => hasBoxScoreData.value ? boxScoreData.value!.scoringDrives : [])
  const currentDrive = computed<Maybe<BoxScore.ScoringDrive>>(() => {
    if (!scoringDrives.value.length) {
      return null
    }

    return scoringDrives.value[scoringDrives.value.length - 1] ?? null
  })
  const lastEvent = computed<Maybe<BoxScore.LastEvent>>(() => hasBoxScoreData.value ? boxScoreData.value?.lastEvent ?? null : null)

  watchEffect(() => {
    srId.value = toValue(srIdRef)
  })

  return {
    awayPoints,
    awayTeam,
    awayTeamRecords,
    clock,
    clockString,
    currentDrive,
    currentPeriodText,
    hasBoxScoreData,
    homePoints,
    homeTeam,
    homeTeamRecords,
    lastEvent,
    periodAmounts,
    scoringPlays,
    scoringData,
    scoringDrives,
    summaryData,
  }
}
